import { ComponentProps, ForwardedRef, forwardRef, isValidElement, MouseEvent, ReactElement, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import cn from 'classnames'
import { IIconProps } from 'components/ui/IconBase'

import s from './ButtonIcon.module.less'

type TColor = 'white' | 'gray' | 'dark' | 'placeholder' | 'accent' | 'normal' | 'error'

export interface IProps extends ComponentProps<'div'> {
  icon: TSvgIconComponent | ReactElement<IIconProps>
  color?: TColor
  containerSize?: ISvgIconProps['containerSize']
  disabled?: boolean
  href?: string
  onClick?: (event: MouseEvent) => void
  rotate?: ISvgIconProps['rotate']
  size?: ISvgIconProps['size']
  target?: string
  to?: string
}

export const ButtonIcon = forwardRef(function ButtonIcon(
  {
    icon: IconFromProps,
    className,
    color = 'normal',
    size = 'nm',
    rotate,
    containerSize,
    disabled,
    onClick,
    to,
    href,
    ...rest
  }: IProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const preparedClassName = cn(className, s.ButtonIcon, s[disabled ? 'disabled' : color])

  const navigate = useNavigate()

  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()

      if (to) {
        navigate({ to })
      }

      if (onClick) {
        onClick(e)
      }
    },
    [navigate, onClick, to]
  )

  const iconElement = isValidElement(IconFromProps) ? (
    IconFromProps
  ) : (
    <IconFromProps containerSize={containerSize} rotate={rotate} size={size} />
  )

  if ((href || to) && !disabled) {
    return (
      // @ts-expect-error
      <a {...(rest as ComponentProps<'a'>)} ref={ref} className={preparedClassName} href={href} onClick={handleClick}>
        {iconElement}
      </a>
    )
  }

  return (
    <div {...rest} ref={ref} className={preparedClassName} onClick={disabled ? undefined : handleClick}>
      {iconElement}
    </div>
  )
})
